/**
 * École Nid Douillet - Parent Portal API Service
 * 
 * API service for parent portal functionality
 */

import { BaseApiService } from './api';
import { ApiResponse } from '@/types';

// Parent portal types
export interface ParentDashboard {
  parent_info: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    communication_preference: 'EMAIL' | 'SMS' | 'BOTH';
    language_preference: 'FRENCH' | 'ARABIC';
  };
  children_summary: {
    total_children: number;
    children: Array<{
      id: string;
      first_name: string;
      last_name: string;
      class_level: string;
      academic_year: string;
    }>;
  };
  payments_summary: {
    total_payments: number;
    pending_amount: number;
    overdue_amount: number;
    recent_payments: Array<{
      id: string;
      amount: number;
      status: string;
      due_date: string;
      payment_type: string;
    }>;
  };
  notifications_summary: {
    unread_count: number;
    recent_notifications: Array<{
      id: string;
      subject: string;
      created_at: string;
      read: boolean;
    }>;
  };
  care_services_summary: {
    enrolled_services: Array<{
      service_name: string;
      child_name: string;
      status: string;
    }>;
  };
}

export interface ChildDetails {
  id: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  gender: 'MALE' | 'FEMALE';
  enrollment_date: string;
  medical_conditions: string[];
  allergies: string[];
  additional_notes: string;
  class_level: {
    level_name: string;
    level_name_arabic: string;
    age_range_min: number;
    age_range_max: number;
  };
  academic_year: {
    year_label: string;
    start_date: string;
    end_date: string;
  };
  payments: Array<{
    id: string;
    payment_type: string;
    amount: number;
    currency: string;
    payment_status: string;
    payment_date: string;
    due_date: string;
    payment_reference: string;
    description: string;
    enrollment_fee: number;
    monthly_fee: number;
    care_service_fee: number;
  }>;
  care_services: Array<{
    id: string;
    service_name: string;
    enrollment_date: string;
    status: string;
    monthly_fee: number;
  }>;
}

export interface PaymentHistory {
  payments: Array<{
    id: string;
    child_name: string;
    payment_type: string;
    amount: number;
    currency: string;
    payment_status: string;
    payment_date: string;
    due_date: string;
    payment_reference: string;
    description: string;
    enrollment_fee: number;
    monthly_fee: number;
    care_service_fee: number;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  summary: {
    total_amount: number;
    pending_amount: number;
    overdue_amount: number;
    completed_amount: number;
  };
}

export interface ParentProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  communication_preference: 'EMAIL' | 'SMS' | 'BOTH';
  language_preference: 'FRENCH' | 'ARABIC';
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  address?: string;
  communication_preference?: 'EMAIL' | 'SMS' | 'BOTH';
  language_preference?: 'FRENCH' | 'ARABIC';
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
}

/**
 * Parent Portal API Service
 */
export class ParentApiService extends BaseApiService {
  /**
   * Get parent dashboard overview
   */
  async getDashboard(): Promise<ApiResponse<ParentDashboard>> {
    return this.get('/parent/dashboard');
  }

  /**
   * Get detailed child information
   */
  async getChildDetails(childId: string): Promise<ApiResponse<ChildDetails>> {
    return this.get(`/parent/children/${childId}`);
  }

  /**
   * Get payment history with filtering
   */
  async getPaymentHistory(params?: {
    page?: number;
    limit?: number;
    child_id?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    payment_type?: string;
  }): Promise<ApiResponse<PaymentHistory>> {
    return this.get('/parent/payments', { params });
  }

  /**
   * Get parent profile
   */
  async getProfile(): Promise<ApiResponse<ParentProfile>> {
    return this.get('/parent/profile');
  }

  /**
   * Update parent profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<ParentProfile>> {
    return this.put('/parent/profile', data);
  }

  /**
   * Get care services for all children
   */
  async getCareServices(params?: {
    child_id?: string;
    status?: string;
  }): Promise<ApiResponse<{
    care_services: Array<{
      id: string;
      child_id: string;
      child_name: string;
      service_name: string;
      enrollment_date: string;
      status: string;
      monthly_fee: number;
      description: string;
    }>;
  }>> {
    return this.get('/parent/care-services', { params });
  }

  /**
   * Get notifications for parent
   */
  async getNotifications(params?: {
    page?: number;
    limit?: number;
    read?: boolean;
  }): Promise<ApiResponse<{
    notifications: Array<{
      id: string;
      subject: string;
      content: string;
      type: string;
      read: boolean;
      created_at: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }>> {
    return this.get('/parent/notifications', { params });
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<ApiResponse<void>> {
    return this.put(`/parent/notifications/${notificationId}/read`);
  }

  /**
   * Get children list for parent
   */
  async getChildren(): Promise<ApiResponse<{
    children: Array<{
      id: string;
      first_name: string;
      last_name: string;
      birth_date: string;
      class_level: string;
      academic_year: string;
      enrollment_date: string;
    }>;
  }>> {
    return this.get('/parent/children');
  }

  /**
   * Download payment receipt
   */
  async downloadReceipt(paymentId: string): Promise<ApiResponse<{ download_url: string }>> {
    return this.get(`/parent/payments/${paymentId}/receipt`);
  }

  /**
   * Request payment reminder
   */
  async requestPaymentReminder(paymentId: string): Promise<ApiResponse<void>> {
    return this.post(`/parent/payments/${paymentId}/remind`);
  }
}

// Export singleton instance
export const parentApi = new ParentApiService();
